// src/components/digital_twin/simulationScenarios.js
// --- UPDATE: v1.0 — single source of truth for Digital Twin simulation scenarios.
// Each scenario declares its UI parameters AND how they map onto the engine's
// real parameters (toEngine). Previously the panel sent UI-only keys such as
// `severity` / `isolation_time` that the engine silently ignored, so every
// run used engine defaults regardless of what the operator set.
import {
  FaTintSlash, FaCogs, FaBolt, FaVial, FaWind, FaWater, FaCloudShowersHeavy,
  FaCloudRain, // --- UPDATE (elevation)
} from 'react-icons/fa';

export const SCENARIO_GROUPS = [
  { id: 'network', label: 'Network failures', description: 'Cascades from one asset through water, road, power and pumps' },
  { id: 'weather', label: 'Weather & climate', description: 'Hurricane, surge and rainfall impacts on all or selected assets', ai: true },
];

/** Asset-scope options shared by the storm scenarios. */
const SCOPE_OPTIONS = ['All assets', 'Selected asset', 'Pumps & power', 'Roads', 'Water network', 'Telemetry'];
const SCOPE_TYPES = {
  'Pumps & power': { layers: ['pump', 'power'] },
  Roads: { layers: ['road'] },
  'Water network': { layers: ['water'] },
  Telemetry: { assetTypes: ['sensor', 'meter', 'smart_meter', 'gateway'] },
};

const scopeToEngine = (scope, ctx) => {
  if (scope === 'Selected asset' && ctx.assetId != null) return { assetIds: [ctx.assetId] };
  return SCOPE_TYPES[scope] || {};
};

const TRACK_OPTIONS = ['Direct hit', '20 km north', '20 km south', '50 km north', 'Over selected asset'];

/** Offset a lat/lng north by km. */
const north = (pt, km) => ({ lat: pt.lat + km / 110.574, lng: pt.lng });

const trackToEngine = (track, ctx) => {
  const base = ctx.centroid;
  if (track === 'Over selected asset' && Number.isFinite(Number(ctx.selectedAsset?.latitude))) {
    return { track: { lat: Number(ctx.selectedAsset.latitude), lng: Number(ctx.selectedAsset.longitude) } };
  }
  if (!base) return {};
  if (track === '20 km north') return { track: north(base, 20) };
  if (track === '20 km south') return { track: north(base, -20) };
  if (track === '50 km north') return { track: north(base, 50) };
  return {}; // direct hit = engine default (selection centroid)
};

const ISOLATION_HOURS = { '15 mins': 0.25, '30 mins': 0.5, '1 hour': 1, '4+ hours': 6 };
const CATEGORY_OPTIONS = ['tropical_storm', 'cat1', 'cat2', 'cat3', 'cat4', 'cat5'];
export const CATEGORY_LABELS = {
  tropical_storm: 'Tropical storm', cat1: 'Category 1', cat2: 'Category 2', cat3: 'Category 3', cat4: 'Category 4', cat5: 'Category 5',
};

export const SCENARIOS = [
  {
    id: 'pipe_burst',
    group: 'network',
    title: 'Main Pipe Burst',
    desc: 'Structural rupture → pressure loss, soil washout and sinkhole risk on nearby roads.',
    icon: FaTintSlash,
    color: 'blue',
    requiresAsset: true,
    targetTypes: ['pipeline', 'main', 'pipe', 'valve', 'hydrant', 'unknown'],
    params: [
      { id: 'severity', label: 'Rupture Severity', type: 'slider', min: 10, max: 100, default: 80, unit: '%' },
      { id: 'isolation_time', label: 'Est. Isolation Time', type: 'select', options: ['15 mins', '30 mins', '1 hour', '4+ hours'], default: '30 mins' },
      { id: 'soil', label: 'Soil Erodibility', type: 'slider', min: 0, max: 100, default: 65, unit: '%' },
    ],
    toEngine: (v) => ({
      basePressureDrop: (60 * v.severity) / 100,
      burstDurationHours: ISOLATION_HOURS[v.isolation_time] ?? 0.5,
      soilErodibility: v.soil / 100,
    }),
  },
  {
    id: 'pump_failure',
    group: 'network',
    title: 'Critical Pump Failure',
    desc: 'Mechanical or electrical failure → downstream pressure and supply loss.',
    icon: FaCogs,
    color: 'orange',
    requiresAsset: true,
    targetTypes: ['pump', 'pump_station', 'booster_pump', 'station', 'well', 'asset', 'unknown'],
    params: [
      { id: 'efficiency_drop', label: 'Efficiency Drop', type: 'slider', min: 20, max: 100, default: 100, unit: '%' },
      { id: 'failure_mode', label: 'Failure Mode', type: 'select', options: ['Mechanical Seize', 'Cavitation', 'Electrical Fault'], default: 'Cavitation' },
    ],
    toEngine: (v) => ({ headLossPsi: (35 * v.efficiency_drop) / 100 }),
  },
  {
    id: 'power_outage',
    group: 'network',
    title: 'Grid Power Outage',
    desc: 'Substation loss → pump shutdown, tank drain and backflow contamination risk.',
    icon: FaBolt,
    color: 'red',
    requiresAsset: true,
    targetTypes: ['substation', 'transformer', 'pump', 'station', 'facility', 'asset', 'unknown'],
    params: [
      { id: 'outage_duration', label: 'Expected Duration', type: 'slider', min: 1, max: 48, default: 4, unit: 'h' },
      { id: 'generator', label: 'Standby Generator Coverage', type: 'slider', min: 0, max: 100, default: 0, unit: '%' },
    ],
    toEngine: (v) => ({ outageHours: v.outage_duration, generatorCoveragePct: v.generator }),
  },
  {
    id: 'contamination',
    group: 'network',
    title: 'Contamination Event',
    desc: 'Intrusion travelling downstream → arrival times, advisories, isolation points.',
    icon: FaVial,
    color: 'purple',
    requiresAsset: true,
    targetTypes: ['pipeline', 'main', 'pipe', 'station', 'well', 'tank', 'unknown'],
    params: [
      { id: 'contaminant_type', label: 'Contaminant Profile', type: 'select', options: ['High Turbidity', 'Low Chlorine', 'pH Imbalance'], default: 'High Turbidity' },
      { id: 'spread_rate', label: 'Flow Velocity', type: 'slider', min: 1, max: 10, default: 4, unit: '×0.2 m/s' },
    ],
    toEngine: (v) => ({
      flowVelocityMps: v.spread_rate * 0.2,
      chlorineResidualMgL: v.contaminant_type === 'Low Chlorine' ? 0.15 : 0.6,
    }),
  },
  {
    id: 'hurricane',
    group: 'weather',
    title: 'Hurricane Impact',
    desc: 'Track-based wind, surge, rain and flooding → physical damage, cost, cascade and restoration plan.',
    icon: FaWind,
    color: 'pink',
    ai: true,
    requiresAsset: false,
    targetTypes: ['unknown'],
    params: [
      { id: 'category', label: 'Storm Category', type: 'select', options: CATEGORY_OPTIONS, labels: CATEGORY_LABELS, default: 'cat3' },
      { id: 'track', label: 'Track', type: 'select', options: TRACK_OPTIONS, default: 'Direct hit' },
      { id: 'heading', label: 'Heading', type: 'slider', min: 0, max: 359, step: 5, default: 300, unit: '°' },
      { id: 'speed', label: 'Forward Speed', type: 'slider', min: 2, max: 25, default: 12, unit: 'kt' },
      { id: 'scope', label: 'Assets', type: 'select', options: SCOPE_OPTIONS, default: 'All assets' },
      { id: 'live', label: 'Blend live weather forecast', type: 'switch', default: true, ai: true },
      { id: 'ensemble', label: 'Uncertainty ensemble', type: 'select', options: ['Off', '25 storms', '100 storms'], default: 'Off', ai: true },
      { id: 'crews', label: 'Repair Crews', type: 'slider', min: 2, max: 100, default: 10, unit: '' },
      { id: 'slr2050', label: '2050 sea level', type: 'switch', default: false },
    ],
    toEngine: (v, ctx) => ({
      category: v.category,
      headingDeg: v.heading,
      forwardSpeedKts: v.speed,
      useLiveWeather: !!v.live,
      crews: v.crews,
      ensembleMembers: v.ensemble === '100 storms' ? 100 : v.ensemble === '25 storms' ? 25 : 0,
      seaLevelHorizon: v.slr2050 ? '2050' : 'current',
      maxResults: 3000,
      ...trackToEngine(v.track, ctx),
      ...scopeToEngine(v.scope, ctx),
    }),
  },
  {
    id: 'flood_forecast',
    group: 'weather',
    title: 'Live Flood Forecast',
    desc: 'Forecast rainfall + surge per asset → time-to-flood, depth, impassable roads.',
    icon: FaCloudShowersHeavy,
    color: 'cyan',
    ai: true,
    requiresAsset: false,
    targetTypes: ['unknown'],
    params: [
      { id: 'horizon', label: 'Forecast Horizon', type: 'slider', min: 12, max: 168, step: 12, default: 72, unit: 'h' },
      { id: 'drainage', label: 'Drainage Capacity', type: 'slider', min: 2, max: 40, default: 12, unit: 'mm/h' },
      { id: 'tide', label: 'High-tide Allowance', type: 'slider', min: 0, max: 100, step: 5, default: 0, unit: 'cm' },
      { id: 'scope', label: 'Assets', type: 'select', options: SCOPE_OPTIONS, default: 'All assets' },
    ],
    toEngine: (v, ctx) => ({
      horizonHours: v.horizon,
      drainageCapacityMmHr: v.drainage,
      tideM: v.tide / 100,
      maxResults: 3000,
      ...scopeToEngine(v.scope, ctx),
    }),
  },
  {
    id: 'climate_flooding',
    group: 'weather',
    title: 'Storm Surge Flooding',
    desc: 'Static surge line + rainfall saturation → inundation and hardening priorities.',
    icon: FaWater,
    color: 'teal',
    requiresAsset: false,
    targetTypes: ['unknown'],
    params: [
      { id: 'surge', label: 'Surge Height', type: 'slider', min: 0, max: 60, default: 20, unit: '×0.1 m' },
      { id: 'rain', label: 'Rainfall', type: 'slider', min: 0, max: 500, step: 10, default: 150, unit: 'mm' },
      { id: 'duration', label: 'Duration', type: 'slider', min: 3, max: 72, default: 12, unit: 'h' },
      { id: 'live', label: 'Use live forecast for rain', type: 'switch', default: false, ai: true },
    ],
    toEngine: (v) => ({
      surgeHeightM: v.surge / 10,
      ...(v.live ? { useLiveWeather: true } : { rainfallMm: v.rain }),
      durationHours: v.duration,
    }),
  },
];

// --- UPDATE (elevation): rainfall → run-off → wells / catch basins / drains on TessaDEM terrain
SCENARIOS.push({
  id: 'rainfall_runoff',
  group: 'weather',
  title: 'Rainfall Run-off & Drainage',
  desc: 'Live or design rain on TessaDEM terrain → run-off into wells & catch basins, capacity (silt-limited), ponding and downhill overflow.',
  icon: FaCloudRain,
  color: 'cyan',
  ai: true,
  requiresAsset: false,
  targetTypes: ['unknown'],
  params: [
    { id: 'live', label: 'Use live rainfall forecast', type: 'switch', default: true, ai: true },
    { id: 'rain', label: 'Design rainfall (when not live)', type: 'slider', min: 10, max: 500, step: 10, default: 120, unit: 'mm' },
    { id: 'duration', label: 'Duration / horizon', type: 'slider', min: 1, max: 72, default: 24, unit: 'h' },
    { id: 'impervious', label: 'Impervious surface', type: 'slider', min: 10, max: 95, step: 5, default: 60, unit: '%' },
    { id: 'wellCap', label: 'Drainage well capacity (clean)', type: 'slider', min: 5, max: 120, step: 5, default: 30, unit: 'L/s' },
    { id: 'basinCap', label: 'Catch basin capacity (clean)', type: 'slider', min: 5, max: 60, step: 5, default: 15, unit: 'L/s' },
    { id: 'scope', label: 'Assets', type: 'select', options: ['All drainage', 'Selected asset'], default: 'All drainage' },
  ],
  toEngine: (v, ctx) => ({
    ...(v.live ? { useLiveWeather: true } : { useLiveWeather: false, rainfallMm: v.rain }),
    durationHours: v.duration,
    imperviousFraction: v.impervious / 100,
    wellCapacityLps: v.wellCap,
    catchBasinCapacityLps: v.basinCap,
    maxResults: 3000,
    ...(v.scope === 'Selected asset' && ctx.assetId != null ? { assetIds: [ctx.assetId] } : {}),
  }),
});
export const isRunoffScenario = (id) => id === 'rainfall_runoff';
// --- END UPDATE

export const SCENARIO_BY_ID = Object.fromEntries(SCENARIOS.map((s) => [s.id, s]));

export const defaultValuesFor = (scenario) =>
  Object.fromEntries((scenario?.params || []).map((p) => [p.id, p.default]));

/** Build the API request for a scenario from UI values + page context. */
export const buildRequest = (scenario, values, ctx = {}) => ({
  scenario: scenario.id,
  assetId: scenario.requiresAsset ? (ctx.assetId ?? 'GLOBAL_NETWORK') : null,
  parameters: scenario.toEngine ? scenario.toEngine(values, ctx) : values,
});

/** Format a parameter value for display. */
export const formatParam = (param, value) => {
  if (param.type === 'switch') return value ? 'On' : 'Off';
  if (param.labels && param.labels[value]) return param.labels[value];
  if (param.unit === '×0.1 m') return `${(value / 10).toFixed(1)} m`;
  if (param.unit === '×0.2 m/s') return `${(value * 0.2).toFixed(1)} m/s`;
  return `${value}${param.unit ? ` ${param.unit}` : ''}`;
};

export const isStormScenario = (id) => id === 'hurricane' || id === 'flood_forecast';
// --- END UPDATE